import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  useBreadcrumb,
  useHierarchicalMenu,
  useSearchBox,
} from 'react-instantsearch';
import { countFormat, pillButtonClassName } from './template';
import {
  CATEGORY_ATTRIBUTES,
  CATEGORY_SEPARATOR,
  slugPathToURL,
  useTaxonomy,
  withQuery,
} from './category-routes';

import './index.css';

// Re-exported for backwards compatibility -- every widget refining on
// `zuper-categories` (the chip nav here, the left-panel top-level list in
// page-search.tsx, and the category-breadcrumb link builder) shares one
// facet configuration/uiState. Defined in category-routes.ts (not here) so
// that module can use them without an import cycle with this one.
export { CATEGORY_ATTRIBUTES, CATEGORY_SEPARATOR };

// A hierarchical menu/breadcrumb item's `value` is the full escaped path
// joined by `CATEGORY_SEPARATOR` -- same shape as the `zuper-categories.
// lvlN` fields on records, which are now SLUG paths (e.g. "trofima >
// galaktokomika-eidi-psygeiou"), not title paths -- see
// connectHierarchicalMenu's `_prepareFacetValues`/`format()` in
// instantsearch.js/algoliasearch-helper. `escapeFacetValue` only ever
// touches a leading "-", which slugs never start with (zuper-data's
// transliteration strips everything down to `[a-z0-9-]`), so splitting it
// back into per-level slugs is safe.
function itemSlugPath(item): string[] {
  return item.value
    .replace(/^\\-/, '-')
    .split(CATEGORY_SEPARATOR)
    .map((segment) => segment.trim());
}

export function CategoryNav() {
  const { query } = useSearchBox();
  const resolveLabel = useTaxonomy();

  const { items: breadcrumbItems } = useBreadcrumb({
    attributes: CATEGORY_ATTRIBUTES,
    separator: CATEGORY_SEPARATOR,
  });

  const { items: menuItems } = useHierarchicalMenu({
    attributes: CATEGORY_ATTRIBUTES,
    separator: CATEGORY_SEPARATOR,
    limit: 20,
    sortBy: ['count:desc', 'name:asc'],
  });

  // Chips show the children of the DEEPEST refined category; at a leaf
  // (refined item with no children) they show its siblings, keeping the
  // leaf highlighted; with nothing refined, the top-level categories.
  let currentLevelItems = menuItems;
  for (;;) {
    const refined = currentLevelItems.find((item) => item.isRefined);
    if (!refined || !refined.data || refined.data.length === 0) break;
    currentLevelItems = refined.data;
  }

  if (breadcrumbItems.length === 0 && currentLevelItems.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {breadcrumbItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm text-gray-500">
          <Link
            to={withQuery('/search', query)}
            className="hover:text-black hover:underline"
          >
            Όλες οι κατηγορίες
          </Link>
          {breadcrumbItems.map((item, index) => {
            // Built from labels, not item.value: useBreadcrumb's LAST item
            // (the current level) always has value null per the
            // InstantSearch contract, and labels are the slug segments in
            // the slug-facet world anyway.
            const slugs = breadcrumbItems
              .slice(0, index + 1)
              .map((i) => i.label.trim());
            return (
              <Fragment key={`${index}-${item.value}`}>
                <span className="text-gray-400">›</span>
                <Link
                  to={withQuery(slugPathToURL(slugs), query)}
                  className="hover:text-black hover:underline"
                >
                  {resolveLabel(slugs)}
                </Link>
              </Fragment>
            );
          })}
        </div>
      )}

      {currentLevelItems.length > 0 && (
        <div className="flex overflow-x-auto whitespace-nowrap gap-2 pb-1 no-scrollbar">
          {currentLevelItems.map((item) => {
            // Clicking the active/refined chip should navigate UP one
            // level (matching the old toggle-off-to-parent refine()
            // behaviour); any other chip navigates INTO it.
            const slugs = itemSlugPath(item);
            const targetSlugs = item.isRefined ? slugs.slice(0, -1) : slugs;
            const to =
              targetSlugs.length === 0
                ? withQuery('/search', query)
                : withQuery(slugPathToURL(targetSlugs), query);

            return (
              <Link
                key={item.value}
                to={to}
                className={pillButtonClassName({
                  variant: item.isRefined ? 'default' : 'grey',
                })}
              >
                {resolveLabel(slugs)}
                <span
                  className={
                    'ml-1 ' +
                    (item.isRefined ? 'text-green-100' : 'text-gray-500')
                  }
                >
                  ({countFormat.format(item.count)})
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
